import React from "react";
import { ChevronRight, Clock, MapPin } from "lucide-react";

export default function DashboardScheduleSection({
  GlassCard,
  longPressHandlers,
  handleWidgetClick,
  isMobile,
  isEditMode,
  sectionHeight,
  icons,
  onNavigate,
  isLongPressTriggered,
  filteredSchedules,
  schedules,
  setSelectedSchedule,
  userProfile,
  getScheduleDDay,
  getScheduleTypeColor,
  getScheduleTypeName,
}) {
  const getScheduleColor = (dept) => {
    if (!dept || dept === "All") return "bg-red-500";
    if (userProfile?.departmentColors?.[dept]) return userProfile.departmentColors[dept];
    return "bg-[#354F38]";
  };

  return (
    <GlassCard
      {...longPressHandlers}
      onClick={handleWidgetClick}
      variant={isMobile ? "solid" : "glass"}
      isMobile={isMobile}
      className={`flex flex-col h-full bg-gradient-to-br from-white to-green-50/30 ${isMobile ? "!rounded-[20px] bg-white p-4" : "p-6 border-b-[8px] border-[#354F38] shadow-xl shadow-[#354F38]/20"}`}
      noHover
    >
      <div className={`flex justify-between items-center relative z-10 shrink-0 ${isMobile ? "mb-4" : "mb-6"}`}>
        <div>
          <h3 className={`font-bold text-[#2C3E30] font-do-hyeon flex items-center gap-2 ${isMobile ? "text-base" : "text-lg"}`}>
            <img src={icons.calendar} alt="cal" className={`drop-shadow-md ${isMobile ? "w-6 h-6" : "w-8 h-8"}`} /> 다가오는 일정
          </h3>
          {sectionHeight > 1 && <p className={`font-bold text-gray-400 mt-1 ml-1 ${isMobile ? "text-[10px]" : "text-xs"}`}>잊지 말고 챙겨주세요!</p>}
        </div>
        <button
          onClick={(e) => {
            if (isEditMode || isLongPressTriggered()) {
              e.stopPropagation();
              return;
            }
            onNavigate("schedule");
          }}
          className={`font-bold text-gray-400 hover:text-[#354F38] transition flex items-center gap-1 bg-white/50 rounded-full hover:bg-white/80 shadow-sm ${isMobile ? "text-[10px] px-2.5 py-1" : "text-xs px-3 py-1.5"}`}
        >
          전체 보기 <ChevronRight size={10} />
        </button>
      </div>
      <div className="space-y-3 relative z-10 flex-1 overflow-y-auto custom-scrollbar pr-1 pt-1">
        {filteredSchedules
          .filter((schedule) => new Date(schedule.date) >= new Date().setHours(0, 0, 0, 0))
          .map((schedule) => (
            <div
              key={schedule.id}
              onClick={() => {
                if (isEditMode) return;
                setSelectedSchedule(schedule);
              }}
              className={`bg-white/70 backdrop-blur-md rounded-[1.2rem] border border-white/60 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.15)] hover:bg-white transition-all cursor-pointer group flex gap-3 items-center relative overflow-hidden transform hover:-translate-y-1 duration-300 ${isMobile ? "p-2.5" : "p-4"}`}
            >
              <div className={`flex flex-col items-center bg-white rounded-xl shadow-[0_4px_10px_rgba(0,0,0,0.1),inset_0_-2px_4px_rgba(0,0,0,0.05)] border border-gray-100 relative group-hover:scale-110 transition-transform overflow-hidden ${isMobile ? "w-10 h-10" : "w-14 h-14"}`}>
                <div className={`w-full h-3 flex items-center justify-center ${getScheduleColor(schedule.department)}`}>
                  <div className="w-6 h-0.5 bg-black/10 rounded-full"></div>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center -mt-0.5">
                  <span className="text-[7px] font-extrabold text-gray-400 uppercase tracking-widest">{new Date(schedule.date).toLocaleString("en-US", { month: "short" })}</span>
                  <span className={`font-black font-do-hyeon leading-none text-gray-800 ${isMobile ? "text-base" : "text-xl"}`}>{new Date(schedule.date).getDate()}</span>
                </div>
              </div>
              <div className="flex-1 min-w-0 py-0.5">
                <div className="flex items-center gap-1.5 mb-0.5">
                  {schedule.department && schedule.department !== "All" && (
                    <span className={`text-[9px] font-black px-1 py-0.5 rounded-md text-white shadow-sm ${getScheduleColor(schedule.department)}`}>
                      {schedule.department}
                    </span>
                  )}
                  {(() => {
                    const dday = getScheduleDDay(schedule.date);
                    if (!dday) return null;

                    return (
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-md text-white shadow-md border border-white/30 ${dday.color}`}>
                        {dday.label}
                      </span>
                    );
                  })()}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md shadow-sm ${getScheduleTypeColor(schedule.type)}`}>
                    {getScheduleTypeName(schedule.type)}
                  </span>
                  <h4 className={`font-bold text-gray-800 truncate group-hover:text-[#354F38] transition ${isMobile ? "text-xs" : "text-sm"}`}>{schedule.title}</h4>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-gray-500 font-medium whitespace-nowrap overflow-hidden">
                  <span className="flex items-center gap-0.5 bg-white/50 px-1 py-0.5 rounded-md">
                    <Clock size={9} className="text-[#354F38]" /> {schedule.startTime}
                  </span>
                  <span className="flex items-center gap-0.5 bg-white/50 px-1 py-0.5 rounded-md truncate">
                    <MapPin size={9} className="text-[#354F38]" /> {schedule.location}
                  </span>
                </div>
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2 group-hover:translate-x-0 transform duration-300">
                <div className="p-2 rounded-full bg-white shadow-sm text-[#354F38]">
                  <ChevronRight size={14} />
                </div>
              </div>
            </div>
          ))}
        {schedules.filter((schedule) => new Date(schedule.date) >= new Date().setHours(0, 0, 0, 0)).length === 0 && (
          <div className="text-center py-10 text-gray-400 text-sm font-bold">등록된 일정이 없습니다.</div>
        )}
      </div>
    </GlassCard>
  );
}
